import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiPhone, FiChevronDown } = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Accueil', path: '/' },
    { name: 'À Propos', path: '/about' },
    { name: 'Services', path: '/services', hasSubmenu: true },
    { name: 'Équipe', path: '/team' },
    { name: 'Contact', path: '/contact' },
  ];

  const serviceLinks = [
    { name: 'Formation Professionnelle', path: '/services/formation' },
    { name: 'Audit et Évaluation', path: '/services/audit' },
    { name: 'Conseil Stratégique', path: '/services/conseil' },
    { name: 'Services Connexes', path: '/services/connexes' },
  ];

  const handleDesktopServicesClick = (e) => {
    e.preventDefault();
    setDesktopServicesOpen(!desktopServicesOpen);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          <Link to="/" className="flex items-center">
            <motion.div whileHover={{ scale: 1.05 }}>
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1768751820608-logo%20MAM.jpg"
                alt="M.A.M Consulting"
                className="h-20 w-auto object-contain rounded-md"
              />
            </motion.div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-2">
            {navItems.map((item) => (
              <div key={item.path} className="relative">
                <div className="flex items-center">
                  <Link
                    to={item.path}
                    className={`relative px-3 py-2 text-sm lg:text-base font-medium transition-colors flex items-center ${
                      location.pathname === item.path ||
                      (item.path !== '/' && location.pathname.startsWith(item.path))
                        ? 'text-blue-700 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                  >
                    {item.name}
                  </Link>

                  {item.hasSubmenu && (
                    <button
                      onClick={handleDesktopServicesClick}
                      className="p-1 ml-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                    >
                      <motion.div
                        animate={{ rotate: desktopServicesOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <SafeIcon 
                          icon={FiChevronDown} 
                          className="w-4 h-4 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                        />
                      </motion.div>
                    </button>
                  )}
                </div>

                {/* Desktop Submenu - Click to show/hide */}
                {item.hasSubmenu && desktopServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl py-2 border border-gray-100 dark:border-gray-700 z-50"
                  >
                    {serviceLinks.map((link, idx) => (
                      <Link
                        key={idx}
                        to={link.path}
                        onClick={() => {
                          setDesktopServicesOpen(false);
                        }}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        {link.name}
                      </Link>
                    ))}
                  </motion.div>
                )}

                {(location.pathname === item.path ||
                  (item.path !== '/' && location.pathname.startsWith(item.path))) && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-700 dark:bg-blue-400"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Desktop CTA Button */}
          <motion.a
            href="tel:+237699187929"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="hidden md:flex items-center px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all shadow-lg"
          >
            <SafeIcon icon={FiPhone} className="mr-2 w-4 h-4" />
            Appeler
          </motion.a>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden p-2">
            <SafeIcon
              icon={isOpen ? FiX : FiMenu}
              className="w-6 h-6 text-gray-700 dark:text-gray-200"
            />
          </button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          initial={false}
          animate={{ height: isOpen ? 'auto' : 0 }}
          className="md:hidden overflow-hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-md"
        >
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => (
              <div key={item.path}>
                <div className="flex items-center justify-between">
                  <Link
                    to={item.path}
                    onClick={() => {
                      setIsOpen(false);
                      setServicesOpen(false);
                    }}
                    className={`flex-1 block px-3 py-2 text-base font-medium rounded-md ${
                      location.pathname === item.path
                        ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
                        : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {item.name}
                  </Link>
                  {item.hasSubmenu && (
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className="px-3 py-2 text-gray-700 dark:text-gray-300"
                    >
                      <motion.div
                        animate={{ rotate: servicesOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                      </motion.div>
                    </button>
                  )}
                </div>

                {/* Mobile Submenu */}
                {item.hasSubmenu && servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="pl-4 space-y-1"
                  >
                    {serviceLinks.map((link, idx) => (
                      <Link
                        key={idx}
                        to={link.path}
                        onClick={() => {
                          setIsOpen(false);
                          setServicesOpen(false);
                        }}
                        className="block px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                      >
                        {link.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </div>
            ))}

            <motion.a
              href="tel:+237699187929"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsOpen(false)}
              className="block w-full mt-4 px-3 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all text-center flex items-center justify-center"
            >
              <SafeIcon icon={FiPhone} className="mr-2 w-4 h-4" />
              Appeler Maintenant
            </motion.a>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Navbar;